import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import {
  Datagrid,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  useDataProvider,
  useListContext,
} from "react-admin";
import {
  APP_TRACK_DEFAULT_EVENT,
  APP_TRACK_QUERY_EVENTS,
  type AppTrack,
} from "../../models/AppTrack";
import type { Cargo } from "../../models/Cargo";
import type { AppDataProvider } from "../../types/dataProvider";
import { formatDate } from "../../utils/common";
import { AgentCargoInfoView, parseCargoRecord } from "../agent/agent_cargo_info";
import {
  AppTrackTruckInfo,
  AppTrackUserInfo,
  buildAppTrackSummary,
  extractCargoIdFromProperties,
  formatPropertiesAsEntries,
  getAppTrackEventLabel,
  isDriverRoleFromProperties,
  parseAppTrackUserInfoList,
  parseDriverTrucks,
} from "./app_track_utils";

/**
 * 列表筛选：user_id + 单一事件（默认 view_cargo_detail）。
 */
const appTrackFilters = [
  <TextInput key="user_id" source="user_id" label="用户 ID" alwaysOn resettable />,
  <SelectInput
    key="event"
    source="event"
    label="事件"
    alwaysOn
    choices={APP_TRACK_QUERY_EVENTS.map((event) => ({
      id: event,
      name: `${getAppTrackEventLabel(event)}（${event}）`,
    }))}
  />,
];

/**
 * 格式化事件时间。
 */
const formatEventTime = (value: unknown): string => {
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    return formatDate(new Date(ms).toISOString());
  }
  if (typeof value === "string" && value) {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber) && /^\d+$/.test(value.trim())) {
      const ms = asNumber < 1e12 ? asNumber * 1000 : asNumber;
      return formatDate(new Date(ms).toISOString());
    }
    return formatDate(value);
  }
  return "-";
};

/**
 * 按用户 ID 批量请求用户信息。
 */
const useUsersByIds = (userIds: string[]) => {
  const dataProvider = useDataProvider() as AppDataProvider;
  const [usersById, setUsersById] = React.useState<Record<string, AppTrackUserInfo>>({});
  const [loading, setLoading] = React.useState(false);
  const requestedIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    const uniqueIds = Array.from(new Set(userIds.filter(Boolean)));
    const missingIds = uniqueIds.filter(
      (id) => !usersById[id] && !requestedIdsRef.current.has(id)
    );
    if (missingIds.length === 0) {
      return;
    }

    missingIds.forEach((id) => requestedIdsRef.current.add(id));
    let cancelled = false;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        const next: Record<string, AppTrackUserInfo> = {};

        try {
          const response = await dataProvider.getMany("users", { ids: missingIds });
          for (const user of parseAppTrackUserInfoList(response?.data)) {
            if (user.id) {
              next[user.id] = user;
            }
          }
        } catch {
          // getMany 不可用时回退到逐条 fetchUserInfo
        }

        const stillMissing = missingIds.filter((id) => !next[id]);
        if (stillMissing.length > 0) {
          const settled = await Promise.allSettled(
            stillMissing.map((id) => dataProvider.fetchUserInfo(id))
          );
          for (const result of settled) {
            if (result.status !== "fulfilled") {
              continue;
            }
            for (const user of parseAppTrackUserInfoList(result.value.data)) {
              if (user.id) {
                next[user.id] = user;
              }
            }
          }
        }

        if (!cancelled && Object.keys(next).length > 0) {
          setUsersById((previous) => ({ ...previous, ...next }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchUsers();

    return () => {
      cancelled = true;
    };
  }, [userIds, usersById, dataProvider]);

  return { usersById, loading };
};

/**
 * 按货源 ID 批量请求货源信息。
 */
const useCargosByIds = (cargoIds: string[]) => {
  const dataProvider = useDataProvider();
  const [cargosById, setCargosById] = React.useState<Record<string, Cargo>>({});
  const [loading, setLoading] = React.useState(false);
  const requestedIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    const uniqueIds = Array.from(new Set(cargoIds.filter(Boolean)));
    const missingIds = uniqueIds.filter(
      (id) => !cargosById[id] && !requestedIdsRef.current.has(id)
    );
    if (missingIds.length === 0) {
      return;
    }

    missingIds.forEach((id) => requestedIdsRef.current.add(id));
    let cancelled = false;

    const fetchCargos = async () => {
      try {
        setLoading(true);
        const next: Record<string, Cargo> = {};

        try {
          const response = await dataProvider.getMany("validCargos", { ids: missingIds });
          const records = Array.isArray(response?.data) ? response.data : [];
          for (const raw of records) {
            const cargo = parseCargoRecord(raw);
            if (cargo?.id) {
              next[cargo.id] = cargo;
            }
          }
        } catch {
          // getMany 不可用时回退到逐条 getOne
        }

        const stillMissing = missingIds.filter((id) => !next[id]);
        if (stillMissing.length > 0) {
          const settled = await Promise.allSettled(
            stillMissing.map((id) => dataProvider.getOne("validCargos", { id }))
          );
          for (const result of settled) {
            if (result.status !== "fulfilled") {
              continue;
            }
            const cargo = parseCargoRecord(result.value.data);
            if (cargo?.id) {
              next[cargo.id] = cargo;
            }
          }
        }

        if (!cancelled && Object.keys(next).length > 0) {
          setCargosById((previous) => ({ ...previous, ...next }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchCargos();

    return () => {
      cancelled = true;
    };
  }, [cargoIds, cargosById, dataProvider]);

  return { cargosById, loading };
};

/**
 * 用户基本信息单元格。
 */
const UserInfoCell: React.FC<{
  userId: string;
  user: AppTrackUserInfo | null;
  loading?: boolean;
}> = ({ userId, user, loading = false }) => {
  if (!userId) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }

  if (loading && !user) {
    return (
      <Typography variant="body2" color="text.secondary">
        加载中...
      </Typography>
    );
  }

  const displayName = user?.name || user?.realName || "未知用户";
  const phone = user?.phone || "";

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 160, maxWidth: 240 }}>
      <Avatar src={user?.avatar || undefined} alt={displayName} sx={{ width: 32, height: 32 }} />
      <Stack spacing={0.1} sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap title={displayName}>
          {displayName}
        </Typography>
        {phone ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            {phone}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" noWrap title={userId}>
            {userId}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

/**
 * 车辆信息展示。
 */
const TruckInfoSection: React.FC<{
  trucks: AppTrackTruckInfo[];
  loading?: boolean;
  error?: string;
}> = ({ trucks, loading = false, error = "" }) => {
  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        正在加载车辆信息...
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="text.secondary">
        {error}
      </Typography>
    );
  }

  if (trucks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        暂无车辆信息
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      {trucks.map((truck, index) => (
        <Box
          key={truck.id || `${truck.plate}-${index}`}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 1,
            p: 1.25,
            bgcolor: "action.hover",
            borderRadius: 1,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              车牌
            </Typography>
            <Typography variant="body2">{truck.plate}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              车牌颜色
            </Typography>
            <Typography variant="body2">{truck.plateColor}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              车型
            </Typography>
            <Typography variant="body2">{truck.truckType}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              车长
            </Typography>
            <Typography variant="body2">{truck.truckLength}</Typography>
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

/**
 * 埋点详情弹窗（用户/货源/车辆可读信息 + 原始 JSON）。
 */
const AppTrackDetailDialog: React.FC<{
  open: boolean;
  record: AppTrack | null;
  user: AppTrackUserInfo | null;
  cargo: Cargo | null;
  userLoading?: boolean;
  cargoLoading?: boolean;
  onClose: () => void;
}> = ({
  open,
  record,
  user,
  cargo,
  userLoading = false,
  cargoLoading = false,
  onClose,
}) => {
  const dataProvider = useDataProvider();
  const [showRaw, setShowRaw] = React.useState(false);
  const [trucks, setTrucks] = React.useState<AppTrackTruckInfo[]>([]);
  const [trucksLoading, setTrucksLoading] = React.useState(false);
  const [trucksError, setTrucksError] = React.useState("");

  const propertyEntries = React.useMemo(
    () => formatPropertiesAsEntries(record?.properties ?? null),
    [record?.properties]
  );

  const isDriverUser = React.useMemo(
    () => isDriverRoleFromProperties(record?.properties ?? null),
    [record?.properties]
  );

  /** 明确是司机，或角色未知时尝试按用户 ID 查司机车辆。 */
  const shouldLoadTrucks = Boolean(
    record?.user_id
    && (isDriverUser || !record?.properties?.user_role)
  );

  /** 仅司机，或确实查到车辆时展示该区块。 */
  const showTruckSection =
    shouldLoadTrucks
    && (isDriverUser || trucksLoading || trucks.length > 0 || Boolean(trucksError));

  React.useEffect(() => {
    if (!open) {
      setShowRaw(false);
      setTrucks([]);
      setTrucksError("");
      setTrucksLoading(false);
    }
  }, [open]);

  /**
   * 打开详情时按用户 ID 查询司机车辆。
   */
  React.useEffect(() => {
    const userId = record?.user_id;
    if (!open || !userId || !shouldLoadTrucks) {
      setTrucks([]);
      setTrucksError("");
      setTrucksLoading(false);
      return;
    }

    let cancelled = false;

    const fetchTrucks = async () => {
      try {
        setTrucksLoading(true);
        setTrucksError("");
        const response = await dataProvider.getOne("drivers", { id: userId });
        if (cancelled) {
          return;
        }
        setTrucks(parseDriverTrucks(response.data));
      } catch {
        if (cancelled) {
          return;
        }
        setTrucks([]);
        setTrucksError(
          isDriverRoleFromProperties(record?.properties ?? null)
            ? "未获取到车辆信息"
            : ""
        );
      } finally {
        if (!cancelled) {
          setTrucksLoading(false);
        }
      }
    };

    void fetchTrucks();

    return () => {
      cancelled = true;
    };
  }, [dataProvider, open, record?.properties, record?.user_id, shouldLoadTrucks]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>行为详情</DialogTitle>
      <DialogContent dividers>
        {record ? (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                事件
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  label={getAppTrackEventLabel(record.event)}
                  color="primary"
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">
                  {record.event || "-"}
                </Typography>
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                事件时间
              </Typography>
              <Typography variant="body2">{formatEventTime(record.event_time)}</Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block" }}>
                用户信息
              </Typography>
              <UserInfoCell
                userId={record.user_id}
                user={user}
                loading={userLoading}
              />
              {user?.realName && user.realName !== user.name ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  真实姓名：{user.realName}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                用户 ID：{record.user_id || "-"}
              </Typography>
            </Box>

            {showTruckSection ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block" }}>
                  车辆信息
                </Typography>
                <TruckInfoSection
                  trucks={trucks}
                  loading={trucksLoading}
                  error={trucksError}
                />
              </Box>
            ) : null}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block" }}>
                货源信息
              </Typography>
              <AgentCargoInfoView
                cargo={cargo}
                loading={cargoLoading && !cargo}
                emptyText={
                  extractCargoIdFromProperties(record.properties)
                    ? `未找到货源 ${extractCargoIdFromProperties(record.properties)}`
                    : "该事件无关联货源"
                }
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: "block" }}>
                事件参数
              </Typography>
              {propertyEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  无
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    gap: 1.25,
                  }}
                >
                  {propertyEntries.map((item) => (
                    <Box key={`${item.key}-${item.value}`} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {item.key}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box>
              <Button size="small" onClick={() => setShowRaw((prev) => !prev)}>
                {showRaw ? "收起原始数据" : "展开原始数据"}
              </Button>
              <Collapse in={showRaw}>
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    overflow: "auto",
                    maxHeight: 280,
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {JSON.stringify(record.raw, null, 2)}
                </Box>
              </Collapse>
            </Box>
          </Stack>
        ) : (
          <Typography color="text.secondary">暂无数据</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * 货源相关用户埋点行为数据表格。
 */
const AppTrackDatagrid: React.FC = () => {
  const { data } = useListContext<AppTrack>();
  const [selected, setSelected] = React.useState<AppTrack | null>(null);

  const userIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const record of Object.values(data ?? {})) {
      if (record?.user_id) {
        ids.add(record.user_id);
      }
    }
    return Array.from(ids);
  }, [data]);

  const cargoIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const record of Object.values(data ?? {})) {
      const cargoId = extractCargoIdFromProperties(record?.properties ?? null);
      if (cargoId) {
        ids.add(cargoId);
      }
    }
    return Array.from(ids);
  }, [data]);

  const { usersById, loading: usersLoading } = useUsersByIds(userIds);
  const { cargosById, loading: cargosLoading } = useCargosByIds(cargoIds);

  const selectedCargoId = extractCargoIdFromProperties(selected?.properties ?? null);

  return (
    <>
      {(usersLoading || cargosLoading) && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            正在加载本页用户 / 货源信息...
          </Typography>
        </Box>
      )}
      <Datagrid
        bulkActionButtons={false}
        rowClick={(_id, _resource, record) => {
          setSelected(record as AppTrack);
          return false;
        }}
      >
        <FunctionField<AppTrack>
          label="用户"
          sortable={false}
          render={(record) => (
            <UserInfoCell
              userId={record?.user_id || ""}
              user={record?.user_id ? usersById[record.user_id] ?? null : null}
              loading={usersLoading}
            />
          )}
        />
        <FunctionField<AppTrack>
          label="事件"
          sortable={false}
          render={(record) => (
            <Stack spacing={0.25} sx={{ minWidth: 120 }}>
              <Chip
                size="small"
                label={getAppTrackEventLabel(record?.event)}
                variant="outlined"
                sx={{ alignSelf: "flex-start" }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                {record?.event || "-"}
              </Typography>
            </Stack>
          )}
        />
        <FunctionField<AppTrack>
          label="事件时间"
          sortable={false}
          render={(record) => formatEventTime(record?.event_time)}
        />
        <FunctionField<AppTrack>
          label="货源"
          sortable={false}
          render={(record) => {
            const cargoId = extractCargoIdFromProperties(record?.properties ?? null);
            if (!cargoId) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }
            const cargo = cargosById[cargoId] ?? null;
            return (
              <AgentCargoInfoView
                cargo={cargo}
                loading={cargosLoading && !cargo}
                compact
                emptyText={`货源 ${cargoId}`}
              />
            );
          }}
        />
        <FunctionField<AppTrack>
          label="摘要"
          sortable={false}
          render={(record) => {
            const text = buildAppTrackSummary(record);
            return (
              <Typography variant="body2" noWrap title={text} sx={{ maxWidth: 260 }}>
                {text}
              </Typography>
            );
          }}
        />
      </Datagrid>
      <AppTrackDetailDialog
        open={Boolean(selected)}
        record={selected}
        user={selected?.user_id ? usersById[selected.user_id] ?? null : null}
        cargo={selectedCargoId ? cargosById[selectedCargoId] ?? null : null}
        userLoading={usersLoading}
        cargoLoading={cargosLoading}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

/**
 * 用户埋点行为数据列表。
 * GET /appTrack，标准分页，强制按事件时间降序；默认按 view_cargo_detail 单一事件查询。
 */
const AppTrackPage: React.FC = () => {
  return (
    <List
      title="用户行为"
      filters={appTrackFilters}
      filterDefaultValues={{ event: APP_TRACK_DEFAULT_EVENT }}
      sort={{ field: "event_time", order: "DESC" }}
      perPage={25}
      exporter={false}
    >
      <AppTrackDatagrid />
    </List>
  );
};

export default AppTrackPage;
